import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import api from "../api";
import "./simulazioni.css";
import CustomSelect from "../CustomSelect";
import useTrainerSearch, {
  trainerToOption,
} from "../hooks/useTrainerSearch";

const EditSimulazioni = () => {
  const [simulazione, setSimulazione] = useState(null);
  const [originalSimulazione, setOriginalSimulazione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const userRole = localStorage.getItem("userRole");
  const [inputErrors, setInputErrors] = useState({});
  const loadTrainerOptions = useTrainerSearch();

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per modificare la simulazione!");

      localStorage.clear();
      navigate("/login");
    } else {
      fetchSimulazione();
    }
  }, [navigate]);

  const isValid = () => {
    const errors = {};

    if (!simulazione.link || simulazione.link.trim() === "") {
      errors.link = "Il collegamento non può essere vuoto!";
    }

    if (
      !simulazione.trainerCodiceFiscale ||
      simulazione.trainerCodiceFiscale.length === 0
    ) {
      errors.trainerCodiceFiscale = "Seleziona un docente!";
    }

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchSimulazione = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/simulazioni/${id}`);
      if (res.status === 200) {
        setSimulazione(res.data);
        setOriginalSimulazione(res.data);
        hydrateSelectedTrainer(res.data?.trainerCodiceFiscale);
      }
    } catch (error) {
      console.error(error);

      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const hydrateSelectedTrainer = async (codiceFiscale) => {
    if (!codiceFiscale) {
      setSelectedTrainer(null);
      return;
    }
    try {
      const response = await api.get("/trainers", {
        params: {
          page: 0,
          size: 1,
          codiceFiscale_eq: codiceFiscale,
        },
      });
      const trainer =
        Array.isArray(response.data?.content) &&
        response.data.content.length > 0
          ? response.data.content[0]
          : Array.isArray(response.data) && response.data.length > 0
          ? response.data[0]
          : null;
      if (trainer) {
        setSelectedTrainer(trainerToOption(trainer));
      } else {
        setSelectedTrainer({
          value: codiceFiscale,
          label: codiceFiscale,
          trainer: { codiceFiscale },
        });
      }
    } catch (error) {
      console.error(error);
      setSelectedTrainer({
        value: codiceFiscale,
        label: codiceFiscale,
        trainer: { codiceFiscale },
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSimulazione((prev) => ({
      ...prev,
      [name]: value,
    }));

    setInputErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
    }));
  };

  const handleSave = async () => {
    if (!isValid()) {
      return;
    }
    try {
      const payload = {
        link: simulazione.link,
        trainerCodiceFiscale: simulazione.trainerCodiceFiscale,
      };

      const res = await api.put(`/simulazioni/${id}`, payload);
      if (res.status === 200) {
        toast.success("Simulazione aggiornata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setIsEditing(false);
        setOriginalSimulazione({ ...simulazione });
        hydrateSelectedTrainer(payload.trainerCodiceFiscale);
      }
    } catch (error) {
      console.error(error);

      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleCancel = () => {
    setSimulazione({ ...originalSimulazione });
    setInputErrors({});
    setIsEditing(false);
    hydrateSelectedTrainer(originalSimulazione?.trainerCodiceFiscale);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const isoDatePart = dateString.slice(0, 10);
    const [year, month, day] = isoDatePart.split("-");
    if (!year || !month || !day) return "";

    return `${day}.${month}.${year}`;
  };

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">
          <i className="fa-solid fa-edit"></i>
          Modifica Simulazione
        </div>
        <div className="controls">
          <button onClick={() => navigate("/simulazioni")} className="back-btn">
            <i className="fa-solid fa-arrow-left"></i>
            Torna alle Simulazioni
          </button>
        </div>
      </div>

      <div className="page-content edit-simulazioni-container">
        {simulazione && (
          <div className="details-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fa-solid fa-video"></i>
                Dettagli Simulazione #{simulazione.id}
              </div>
              <div className="card-actions">
                {!isEditing && (
                  <button
                    className="edit-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    <i className="fa-solid fa-edit"></i>
                    Modifica
                  </button>
                )}
              </div>
            </div>

            <div className="card-content">
              <div className="info-sections">
                <div className="info-section">
                  <div className="section-header">
                    <i className="fa-solid fa-info-circle"></i>
                    Informazioni Principali
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <label className="info-label">Data Inserimento:</label>
                      <span className="value-text">
                        {formatDate(simulazione?.dataInserimento)}
                      </span>
                    </div>

                    <div className="info-item">
                      <label className="info-label">Autore:</label>
                      <span className="value-text">
                        {simulazione?.autore || "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <div className="section-header">
                    <i className="fa-solid fa-link"></i>
                    Dettagli Simulazione
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <label className="info-label">
                        Link alla Simulazione:
                      </label>
                      <div className="info-value">
                        {isEditing ? (
                          <input
                            name="link"
                            value={simulazione.link || ""}
                            onChange={handleChange}
                            className="modern-input"
                            placeholder="Inserisci il link alla simulazione"
                          />
                        ) : (
                          <span className="value-text">
                            <a
                              href={simulazione?.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {simulazione?.link || "—"}
                            </a>
                          </span>
                        )}
                        {inputErrors.link && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {inputErrors.link}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="info-item">
                      <label className="info-label">Docente:</label>
                      <div className="info-value">
                        {isEditing ? (
                          <CustomSelect
                            name="trainerCodiceFiscale"
                            loadOptions={loadTrainerOptions}
                            value={selectedTrainer}
                            onChange={(selected) => {
                              setSelectedTrainer(selected);
                              handleChange({
                                target: {
                                  name: "trainerCodiceFiscale",
                                  value:
                                    selected?.trainer?.codiceFiscale ||
                                    selected?.value ||
                                    "",
                                },
                              });
                            }}
                            placeholder="Seleziona un docente"
                            className="modern-select"
                          />
                        ) : (
                          <span className="value-text">
                            {(() => {
                              const trainerInfo =
                                selectedTrainer?.trainer ??
                                simulazione?.trainer ??
                                null;
                              if (trainerInfo) {
                                return `${trainerInfo.nome ?? ""} ${
                                  trainerInfo.cognome ?? ""
                                } (${trainerInfo.codiceFiscale ?? ""})`.trim();
                              }
                              return simulazione?.trainerCodiceFiscale || "—";
                            })()}
                          </span>
                        )}
                        {inputErrors.trainerCodiceFiscale && (
                          <span className="error-message">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            {inputErrors.trainerCodiceFiscale}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="edit-actions">
                  <button className="btn-secondary" onClick={handleCancel}>
                    <i className="fa-solid fa-times"></i>
                    Annulla
                  </button>
                  <button className="btn-primary" onClick={handleSave}>
                    <i className="fa-solid fa-save"></i>
                    Salva Modifiche
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditSimulazioni;
